import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANG_TO_PREFIX, stripLangPrefix } from "@/lib/langRoute";

// Production canonical base. Preview env still gets correct relative paths.
const PROD_BASE = "https://dannyclans.solutions";

const LOCALE_MAP = { en: "en_US", vi: "vi_VN", zh: "zh_CN", ko: "ko_KR", fr: "fr_FR", de: "de_DE" };

const buildBreadcrumbJsonLd = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url?.startsWith("http") ? item.url : `${PROD_BASE}${item.url || "/"}`,
    })),
  };
};

const buildFaqJsonLd = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q || item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a || item.answer,
      },
    })),
  };
};

// Maximally-private robots directive used on admin surfaces. Combines every
// modern signal search engines respect so admin URLs stay out of every
// index, cache, translation, and preview pipeline — including
// image / video / snippet caches.
const NOINDEX_DIRECTIVE = "noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate, max-snippet:0, max-image-preview:none, max-video-preview:0";

/**
 * Per-page SEO component. Sets title, description, canonical URL,
 * Open Graph + Twitter cards, hreflang alternates, and optional
 * JSON-LD structured-data blocks (Article/Custom + Breadcrumb + FAQPage).
 *
 * When ``noindex`` is true the component ALSO suppresses every
 * discoverability surface (hreflang alternates, OG/Twitter tags,
 * JSON-LD, canonical) so admin/private routes leave no trail for
 * crawlers, link-preview bots, or LLM indexers.
 */
export const Seo = ({
  title,
  description,
  keywords,
  path,
  image = "https://dannyclans.solutions/screenshots/og-card.webp",
  imageAlt,
  type = "website",
  jsonLd,
  breadcrumbs,
  faq,
  noindex = false,
  ogTitle,
  ogDescription,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  const cleanPath = stripLangPrefix(path || location.pathname);
  const prefix = LANG_TO_PREFIX[lang] || "";
  const canonical = `${PROD_BASE}${prefix ? `/${prefix}` : ""}${cleanPath === "/" ? "/" : cleanPath}`;

  const fullTitle = title ? `${title} — Danny Clans Media` : "Danny Clans Media — One Dashboard. Every Ad Channel. One Wallet.";

  // Social share cards can carry a different hook than the Google SERP.
  // Falls back to the SEO title / description if the caller doesn't
  // provide dedicated OG copy.
  const socialTitle = ogTitle ? `${ogTitle} — Danny Clans Media` : fullTitle;
  const socialDescription = ogDescription || description || "";

  // Admin/private surfaces emit a minimal head — no canonical, no
  // hreflang, no OG/Twitter cards, no JSON-LD. Only a strong robots
  // directive and a title.
  if (noindex) {
    return (
      <Helmet prioritizeSeoTags>
        <html lang={lang} />
        <title>{fullTitle}</title>
        <meta name="robots" content={NOINDEX_DIRECTIVE} />
        <meta name="googlebot" content={NOINDEX_DIRECTIVE} />
        <meta name="bingbot" content={NOINDEX_DIRECTIVE} />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
    );
  }

  const breadcrumbLd = buildBreadcrumbJsonLd(breadcrumbs);
  const faqLd = buildFaqJsonLd(faq);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || "DC Dashboard provisions agency-grade ad accounts on every major platform, funded from a single prepaid wallet. Hong Kong SAR incorporated."} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />

      {/* Hreflang alternates */}
      {Object.entries(LANG_TO_PREFIX).map(([lc, pre]) => {
        const alt = `${PROD_BASE}${pre ? `/${pre}` : ""}${cleanPath === "/" ? "/" : cleanPath}`;
        return <link key={lc} rel="alternate" hrefLang={lc} href={alt} />;
      })}
      <link rel="alternate" hrefLang="x-default" href={`${PROD_BASE}${cleanPath === "/" ? "/" : cleanPath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Danny Clans Media" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:locale" content={LOCALE_MAP[lang] || "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={image} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {/* JSON-LD blocks. Each rendered as a separate <script> so multiple
          structured-data types can coexist per Google's guidance. */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {breadcrumbLd && (
        <script type="application/ld+json" data-testid="jsonld-breadcrumb">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}
      {faqLd && (
        <script type="application/ld+json" data-testid="jsonld-faq">
          {JSON.stringify(faqLd)}
        </script>
      )}
    </Helmet>
  );
};

export default Seo;
